import { type ComponentPropsWithoutRef } from "react";

import { RightOutlined } from "@ant-design/icons";
import { cx } from "class-variance-authority";

import { StatusBadge, type StatusBadgeStatus } from "../StatusBadge";

import * as theme from "./DetailedStatus.css";

export interface DetailedStatusProps extends Omit<
    ComponentPropsWithoutRef<"div">,
    "onClick"
> {
    status: StatusBadgeStatus;
    // Title segments, joined with " · " (e.g. ["In progress", "High friction"]).
    labels: string[];
    // Optional secondary segments, joined with " · " (e.g. ["Overdue", "Rejected"]).
    details?: string[];
    showChevron?: boolean;
    interactive?: boolean;
    onClick?: () => void;
}

export function DetailedStatus({
    status,
    labels,
    details,
    showChevron = true,
    interactive = true,
    onClick,
    className,
    ...props
}: DetailedStatusProps) {
    const hasDetails = !!details?.length;
    const clickable = !!onClick;
    return (
        <div
            className={cx(
                theme.root,
                interactive && theme.interactive,
                className
            )}
            onClick={onClick}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            // Keyboard parity for the clickable cell.
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            {...props}
        >
            <div className={theme.info}>
                <StatusBadge status={status}>{labels.join(" · ")}</StatusBadge>
                {hasDetails && (
                    <span className={theme.details}>{details.join(" · ")}</span>
                )}
            </div>
            {showChevron && <RightOutlined className={theme.chevron} />}
        </div>
    );
}
